import { useIdle } from '@uidotdev/usehooks';
import { useMemo } from 'react';

import { CHAIN_IDS } from '@/constants/chain';
import { MATCHA_POLLING_IDLE_TIMEOUT_MS } from '@/constants/polling';

import type { Price, Quote } from '@/utils/0x.types';
import { ChainAddress } from '@/utils/models';

import { useMatchaWallets } from './useMatchaWallets';
import { useSolanaSwapQuote } from './useSolanaSwapQuote';
import { useSwapPrice } from './useSwapPrice';
import { useSwapQuote } from './useSwapQuote';
import { useTokenBalance } from './useTokenBalance';

/**
 * Subset of React Query options accepted by the swap aggregator and
 * forwarded to each leaf hook. Restricted to scalar values so the type
 * works for every leaf regardless of its data type (the function-form
 * of `refetchInterval`/`staleTime` are generic over `TData` and would
 * not unify across the three leaves).
 */
export type SwapQueryOptions = {
  refetchInterval?: number | false;
  refetchIntervalInBackground?: boolean;
  staleTime?: number;
  gcTime?: number;
  retry?: boolean | number;
};

export interface UseSwapParams {
  query: SwapArgs | null;
  options: SwapQueryOptions;
  isGasless: boolean;
  telemetry: { buyAmountDecimal: string; buyAmountUsd: number };
}

export interface SwapArgs {
  /** Token address to buy */
  buyTokenAddress: ChainAddress;
  /** The chain Id of the token to buy */
  buyTokenChainId: number;
  /** Amount of sell token in wei */
  sellAmount: string;
  /** Token address to sell */
  sellTokenAddress: ChainAddress;
  /** The chain Id of the token to sell */
  sellTokenChainId: number;
}

/**
 * Aggregates the standard swap `/price`, `/quote`, and Solana quote
 * fetchers. Returns the data from whichever leaf is currently active
 * (the `canFetch*` flags are mutually exclusive) so the caller can
 * react via `useEffect` on the returned data.
 */
export function useSwap({ query, options, isGasless, telemetry }: UseSwapParams) {
  const { ethereumWallet } = useMatchaWallets();
  const { sellTokenChainId, buyTokenChainId } = query ?? {};
  const leafParams = useMemo(
    () =>
      query
        ? {
            ...query,
            isGasless,
            telemetry: {
              buyAmountDecimal: telemetry.buyAmountDecimal,
              buyAmountUsd: telemetry.buyAmountUsd,
            },
          }
        : null,
    [query, isGasless, telemetry.buyAmountDecimal, telemetry.buyAmountUsd],
  );
  const { data: balance } = useTokenBalance({
    address: ethereumWallet?.address,
    tokenAddress: query?.sellTokenAddress,
    chainId: query?.sellTokenChainId,
  });
  const hasInsufficientBalance = !balance || balance.value < BigInt(query?.sellAmount ?? '0');
  const isSolana = sellTokenChainId === CHAIN_IDS.SOLANA || buyTokenChainId === CHAIN_IDS.SOLANA;
  const isIdle = useIdle(MATCHA_POLLING_IDLE_TIMEOUT_MS);
  const shouldFetch = !isIdle;

  const canSwap = !!(query && parseInt(query.sellAmount, 10) > 0);
  const canSwapSingleChain = canSwap && sellTokenChainId === buyTokenChainId;
  // Only one of the three "canFetch" flags below is ever true at once.
  const canFetchQuote =
    shouldFetch && canSwapSingleChain && !!ethereumWallet?.address && !hasInsufficientBalance;
  const canFetchPrice = shouldFetch && canSwapSingleChain && !canFetchQuote;
  const canFetchSolanaQuote = shouldFetch && canSwapSingleChain && isSolana;

  const {
    data: swapPriceData,
    error: swapPriceError,
    isLoading: isLoadingSwapPrice,
    isFetching: isFetchingSwapPrice,
  } = useSwapPrice(leafParams, { ...options, enabled: canFetchPrice });

  const {
    data: swapQuoteData,
    error: swapQuoteError,
    isLoading: isLoadingSwapQuote,
    isFetching: isFetchingSwapQuote,
  } = useSwapQuote(leafParams, { ...options, enabled: canFetchQuote });

  const {
    data: solanaSwapQuoteData,
    error: solanaSwapQuoteError,
    isLoading: isLoadingSolanaQuote,
    isFetching: isFetchingSolanaQuote,
  } = useSolanaSwapQuote(query, { ...options, enabled: canFetchSolanaQuote });

  const data: Price | Quote | undefined =
    swapQuoteData ?? swapPriceData ?? solanaSwapQuoteData ?? undefined;

  return {
    data,
    error: swapPriceError || swapQuoteError || solanaSwapQuoteError,
    isLoading: isLoadingSwapPrice || isLoadingSwapQuote || isLoadingSolanaQuote,
    isFetching: isFetchingSwapPrice || isFetchingSwapQuote || isFetchingSolanaQuote,
  };
}
